import { EPOCHS, WORLD_INIT, epochIndexForFaith, nextEpoch } from '../config';
import type { WorldStateSnapshot, Stone } from '../types';

type Listener = (s: WorldState) => void;

/**
 * 世界权威状态:香火/纪元/人口/天气/时间/石碑。
 * Phase 0 里由模拟器驱动;Phase 3 起由服务端权威快照驱动。
 */
export class WorldState {
  faith = WORLD_INIT.faith;
  population = WORLD_INIT.population;
  onlineGods = WORLD_INIT.onlineGods;
  epochIndex = 0;
  weather: WorldStateSnapshot['weather'] = 'clear';
  timeOfDay: WorldStateSnapshot['time'] = 'night';
  stones: Stone[] = [];
  seenTypes: string[] = [];   // 全服图鉴:世界见证过的神迹类型(服务端权威,人人一致)

  private listeners = new Set<Listener>();
  /** 当纪元跃迁时回调(用于触发大神迹 + 建筑升级) */
  onEpochAdvance: ((from: number, to: number) => void) | null = null;

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() { for (const fn of this.listeners) fn(this); }

  addFaith(delta: number) {
    if (delta <= 0) return;
    const before = this.epochIndex;
    this.faith += delta;
    const after = epochIndexForFaith(this.faith);
    if (after > before) {
      this.epochIndex = after;
      if (this.onEpochAdvance) this.onEpochAdvance(before, after);
    }
    this.emit();
  }

  setPopulation(n: number) { this.population = Math.max(0, n); this.emit(); }
  addPopulation(n: number) { this.population = Math.max(0, this.population + n); this.emit(); }
  setOnline(n: number) { this.onlineGods = Math.max(1, n); this.emit(); }
  setWeather(w: WorldStateSnapshot['weather']) { this.weather = w; this.emit(); }
  setTime(t: WorldStateSnapshot['time']) { this.timeOfDay = t; this.emit(); }

  carveStone(text: string, author: string, midHash?: string): Stone {
    const s: Stone = { id: 's' + (this.stones.length + 1), text, author, midHash, createdAt: this.stones.length };
    this.stones.push(s);
    this.emit();
    return s;
  }

  /**
   * 应用服务端权威快照(Phase 3)。直接置数(非增量),使多端收敛到同一世界。
   * 返回纪元是否推进(true 时 World 需要揭建筑;大神迹由服务端事件另行播放)。
   */
  applySnapshot(s: {
    faith?: number; population?: number; online?: number; epochIndex?: number;
    weather?: WorldStateSnapshot['weather']; stones?: { id?: string; text: string; author: string; midHash?: string }[];
    seenTypes?: string[];
  }): boolean {
    if (Array.isArray(s.seenTypes)) this.seenTypes = s.seenTypes;   // 全服图鉴权威集
    if (typeof s.faith === 'number') this.faith = s.faith;
    if (typeof s.population === 'number') this.population = Math.max(0, s.population);
    if (typeof s.online === 'number') this.onlineGods = Math.max(1, s.online);
    if (s.weather) this.weather = s.weather;
    let advanced = false;
    if (typeof s.epochIndex === 'number' && s.epochIndex !== this.epochIndex) {
      advanced = s.epochIndex > this.epochIndex;
      this.epochIndex = s.epochIndex;
    }
    if (s.stones && s.stones.length > this.stones.length) {
      for (let i = this.stones.length; i < s.stones.length; i++) {
        const st = s.stones[i];
        this.stones.push({ id: st.id ?? 's' + (i + 1), text: st.text, author: st.author, midHash: st.midHash, createdAt: i });
      }
    }
    this.emit();
    return advanced;
  }

  get epoch() { return EPOCHS[this.epochIndex]; }
  get epochName() { return EPOCHS[this.epochIndex].name; }
  get epochOrdinal() { return this.epochIndex + 1; }
  next() { return nextEpoch(this.faith); }

  /** 香火进度百分比(当前纪元 → 下一里程碑) */
  faithProgress(): number {
    const cur = this.epoch.faith;
    const nx = this.next();
    if (!nx) return 1;
    return Math.min(1, (this.faith - cur) / (nx.faith - cur));
  }

  snapshot(): WorldStateSnapshot {
    return {
      epoch: this.epochIndex,
      epochKey: this.epoch.key,
      epochName: this.epochName,
      weather: this.weather,
      time: this.timeOfDay,
      faith: this.faith,
      population: this.population,
      onlineGods: this.onlineGods,
    };
  }
}
